import { observer } from "mobx-react-lite";
import { LearningStore } from "./learning_store";
import { AttemptSummary } from "./learning_model";

export function Pagination({
  page,
  busy,
  hasNext,
  onPage,
}: {
  page: number;
  busy: boolean;
  hasNext: boolean;
  onPage: (page: number) => void;
}) {
  if (page === 0 && !hasNext) return null;
  return (
    <nav className="portal-actions pagination" aria-label="Страницы списка">
      <button disabled={busy || page === 0} onClick={() => onPage(page - 1)}>
        Назад
      </button>
      <span>Страница {page + 1}</span>
      <button disabled={busy || !hasNext} onClick={() => onPage(page + 1)}>
        Далее
      </button>
    </nav>
  );
}

export function PageHeading({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <header className="page-heading">
      <h1>{title}</h1>
      {description && <p>{description}</p>}
    </header>
  );
}

export function AttemptRows({
  attempts,
  go,
}: {
  attempts: AttemptSummary[];
  go: (id: string) => void;
}) {
  return (
    <div className="attempt-list">
      {attempts.map((attempt) => (
        <article className="attempt-row" key={attempt.id}>
          <time>
            {new Date(
              attempt.completedAt || attempt.startedAt,
            ).toLocaleDateString("ru-RU", { timeZone: "Europe/Moscow" })}
          </time>
          <button className="text-link" onClick={() => go(attempt.id)}>
            {attempt.title}
          </button>
          <strong>
            {attempt.status === "completed"
              ? `${attempt.score} / ${attempt.maxScore}`
              : attempt.expired
                ? "Срок истёк"
                : "В процессе"}
          </strong>
          {attempt.status === "completed" && (
            <strong
              className={
                attempt.maxScore &&
                (attempt.score || 0) / attempt.maxScore >= 0.8
                  ? "score-good"
                  : "score-average"
              }
            >
              {attempt.maxScore
                ? `${Math.round(((attempt.score || 0) / attempt.maxScore) * 100)}%`
                : "—"}
            </strong>
          )}
        </article>
      ))}
    </div>
  );
}

export const HistoryScreen = observer(
  ({ store, go }: { store: LearningStore; go: (id?: string) => void }) => (
    <>
      <PageHeading title="Все результаты" />
      <section className="portal-card">
        {!store.attempts.length && !store.busy && (
          <p>
            {store.historyPage === 0
              ? "Вы ещё не начинали тесты."
              : "На этой странице нет попыток."}
          </p>
        )}
        <AttemptRows attempts={store.attempts} go={go} />
        <Pagination
          page={store.historyPage}
          busy={store.busy}
          hasNext={store.attempts.length === 10}
          onPage={(page) => store.lobby(store.examPage, page)}
        />
      </section>
    </>
  ),
);

export const LobbyScreen = observer(
  ({
    store,
    go,
    variants = false,
  }: {
    store: LearningStore;
    go: (id?: string) => void;
    variants?: boolean;
  }) => {
    const exams = variants ? store.variants : store.exams;
    const page = variants ? store.variantPage : store.examPage;
    return (
      <>
        <PageHeading
          title={variants ? "Варианты прошлых лет" : "Текущие тесты"}
        />
        {!variants &&
          store.attempts.some(
            (attempt) => attempt.status === "in_progress",
          ) && (
            <section className="portal-card">
              <h2>Продолжить решение</h2>
              <AttemptRows
                attempts={store.attempts.filter(
                  (attempt) => attempt.status === "in_progress",
                )}
                go={go}
              />
            </section>
          )}
        <section
          className="exam-grid"
          aria-label={variants ? "Варианты" : "Доступные тесты"}
        >
          {!exams.length && !store.busy && (
            <div className="portal-card portal-empty">
              <h2>
                {page
                  ? "На этой странице нет тестов"
                  : variants
                    ? "Варианты пока не добавлены"
                    : "Тесты ещё не опубликованы"}
              </h2>
            </div>
          )}
          {exams.map((exam) => (
            <article className="portal-card exam-card" key={exam.id}>
              {variants && exam.year && (
                <p className="exam-year">{exam.year}</p>
              )}
              <h2>{exam.title}</h2>
              <p className="portal-muted">
                {exam.taskCount} заданий
                {exam.totalScore != null ? ` · ${exam.totalScore} баллов` : ""}
              </p>
              {exam.expiresAt && (
                <p className="portal-muted">
                  До{" "}
                  {new Date(exam.expiresAt).toLocaleString("ru-RU", {
                    timeZone: "Europe/Moscow",
                  })}
                </p>
              )}
              <button
                className="portal-primary"
                disabled={store.busy}
                onClick={async () => {
                  const id = await store.start(exam.id);
                  if (id) go(id);
                }}
              >
                Начать / продолжить
              </button>
            </article>
          ))}
        </section>
        <Pagination
          page={page}
          busy={store.busy}
          hasNext={exams.length === 10}
          onPage={(next) =>
            variants
              ? store.listVariants(next)
              : store.lobby(next, store.historyPage)
          }
        />
      </>
    );
  },
);
